/**
 * Error boundary: catches render/lifecycle errors and shows a fallback UI.
 * Uses ErrorWithStatusView for status errors (e.g. 401), ErrorFallbackView otherwise.
 */
import React from "react";
import { ErrorFallbackView, ErrorWithStatusView } from "./components";
import type { ErrorBoundaryProps, ErrorBoundaryState } from "./types";

function parseCaughtError(error: Error & { message?: string }): ErrorBoundaryState["error"] {
  try {
    return JSON.parse(error.message) as ErrorBoundaryState["error"];
  } catch {
    const message =
      error?.message && String(error.message).trim()
        ? String(error.message)
        : "Something went wrong!";
    return { message };
  }
}

function getDetailMessage(error: ErrorBoundaryState["error"]): string {
  if (error && typeof error === "object" && "message" in error) return error.message;
  if (error != null) return String(error);
  return "";
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: { message: "" }, errorInfo: null };
  }

  componentDidCatch(
    error: Error & { message?: string },
    errorInfo: { componentStack: string }
  ): void {
    const parsed = parseCaughtError(error);
    this.setState({ error: parsed, errorInfo });
    console.error("[ErrorBoundary]", error, errorInfo?.componentStack);
  }

  resetError = (): void => {
    this.setState({ error: { message: "" }, errorInfo: null });
  };

  render(): React.ReactNode {
    const { error, errorInfo } = this.state;
    const { variant = "fullpage", homeTo, children } = this.props;

    if (!errorInfo) {
      return children;
    }

    if (error.status) {
      return <ErrorWithStatusView message={error.message} status={error.status} />;
    }

    return (
      <ErrorFallbackView
        message={getDetailMessage(error)}
        componentStack={errorInfo.componentStack}
        onRetry={() => window.location.reload()}
        variant={variant}
        homeTo={homeTo}
      />
    );
  }
}

export default ErrorBoundary;
